import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { View, StyleSheet, TextInput, Platform } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import useDateTime from "@/hooks/useDateTime";
import { parseFlexibleDate } from "@/lib/dateParser";
import ContextMenu from "@/components/ContextMenu";
import { CompactDateTimePicker } from "./CompactDateTimePicker";
import { SegmentRow } from "./SegmentRow";
import type { SegmentTableProps, TimeSegment } from "./types";

// Generate a simple UUID
function generateId(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Create a default new segment (worked, 1 hour duration, billable and payable)
function createDefaultSegment(): TimeSegment {
  return {
    id: generateId(),
    type: "worked",
    duration: 1, // 1 hour default
    billable: true,
    payable: true,
  };
}

// Calculate totals from segments
function calculateTotals(segments: TimeSegment[]): {
  totalHours: number;
  workedHours: number;
  mealHours: number;
  breakHours: number;
  nonWorkedHours: number;
} {
  let workedHours = 0;
  let mealHours = 0;
  let breakHours = 0;
  let nonWorkedHours = 0;

  for (const segment of segments) {
    const hours = segment.duration;

    if (segment.type === "worked") {
      workedHours += hours;
    } else if (segment.type === "meal") {
      mealHours += hours;
    } else if (segment.type === "break") {
      breakHours += hours;
    } else {
      nonWorkedHours += hours;
    }
  }

  return {
    totalHours: workedHours + mealHours + breakHours + nonWorkedHours,
    workedHours,
    mealHours,
    breakHours,
    nonWorkedHours,
  };
}

// Format date-time for display (e.g., "Jan 15, 8:00 AM")
function formatDateTimeDisplay(isoString: string, use24h: boolean, toUserTimezone: (d: Date) => Date): string {
  const date = toUserTimezone(new Date(isoString));
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();

  let timeStr: string;
  if (use24h) {
    timeStr = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
  } else {
    const h12 = hours % 12 || 12;
    const ampm = hours >= 12 ? "PM" : "AM";
    timeStr = `${h12}:${minutes.toString().padStart(2, "0")} ${ampm}`;
  }

  return `${month} ${day}, ${timeStr}`;
}

export function SegmentTable({
  startTime,
  segments,
  onStartTimeChange,
  onSegmentsChange,
  onMenuActive,
}: SegmentTableProps) {
  const { colors } = useTheme();
  const { settings, toUserTimezone, fromUserTimezone } = useDateTime();
  const use24h = settings.timeFormat === "24";

  // Track which row has an open menu (by segment id)
  const [activeRowId, setActiveRowId] = useState<string | null>(null);
  const [startPickerOpen, setStartPickerOpen] = useState(false);
  const startTimeBtnRef = useRef<View>(null);

  // Track which segment is being dragged
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

  // Calculate summary
  const summary = useMemo(() => calculateTotals(segments), [segments]);

  // Format start time display
  const startDisplay = formatDateTimeDisplay(startTime, use24h, toUserTimezone);

  // Read-only end time: the start time carried forward by the total of every
  // segment's duration (worked + non-worked alike).
  const endDisplay = useMemo(() => {
    const endMs = new Date(startTime).getTime() + summary.totalHours * 3_600_000;
    return formatDateTimeDisplay(new Date(endMs).toISOString(), use24h, toUserTimezone);
  }, [startTime, summary.totalHours, use24h, toUserTimezone]);

  // Editable text for the natural-input start-time field; re-syncs to the
  // canonical display whenever the underlying start time changes.
  const [startText, setStartText] = useState(startDisplay);
  useEffect(() => {
    setStartText(startDisplay);
  }, [startDisplay]);

  // Track if any menu is open (including start time picker)
  const isAnyMenuOpen = activeRowId !== null || startPickerOpen;

  // Notify parent when menu active state changes
  useEffect(() => {
    onMenuActive?.(isAnyMenuOpen);
  }, [isAnyMenuOpen, onMenuActive]);

  const handleStartTimeChange = (date: Date) => {
    const utcDate = fromUserTimezone(date);
    onStartTimeChange(utcDate.toISOString());
  };

  // Commit natural date/time text ("feb 5 8am", "7/23 13:00", …). Parsed in the
  // user-tz frame (same as the picker); reverts to the display on a bad parse.
  const commitStartText = (text: string) => {
    if (!text.trim()) {
      setStartText(startDisplay);
      return;
    }
    const parsed = parseFlexibleDate(text, { mode: "datetime", baseDate: toUserTimezone(new Date(startTime)) });
    if (parsed) {
      handleStartTimeChange(parsed);
    } else {
      setStartText(startDisplay);
    }
  };

  const handleAddSegment = () => {
    const newSegment = createDefaultSegment();
    onSegmentsChange([...segments, newSegment]);
  };

  const handleUpdateSegment = (updatedSegment: TimeSegment) => {
    const updatedSegments = segments.map((seg) => (seg.id === updatedSegment.id ? updatedSegment : seg));
    onSegmentsChange(updatedSegments);
  };

  const handleDeleteSegment = (id: string) => {
    const filteredSegments = segments.filter((seg) => seg.id !== id);
    onSegmentsChange(filteredSegments);
  };

  // Drag handlers for segment reordering
  const handleDragStart = (index: number) => {
    setDraggingIndex(index);
  };

  const handleReorder = (fromIndex: number, toIndex: number) => {
    setDraggingIndex(null);

    // Clamp toIndex to valid range
    const clampedToIndex = Math.max(0, Math.min(segments.length - 1, toIndex));

    if (fromIndex === clampedToIndex) return;

    // Create a copy and move the segment
    const newSegments = [...segments];
    const [movedSegment] = newSegments.splice(fromIndex, 1);
    newSegments.splice(clampedToIndex, 0, movedSegment);
    onSegmentsChange(newSegments);
  };

  // Create stable callback for menu toggle per segment
  const createMenuToggleHandler = useCallback(
    (segmentId: string) => (isOpen: boolean) => {
      setActiveRowId(isOpen ? segmentId : null);
    },
    [],
  );

  // Calculate container elevation based on whether any row has an open menu
  const containerElevation = isAnyMenuOpen ? 1 : -1;

  return (
    <View style={styles.container}>
      {/* Header with global start time */}
      <View style={[styles.header, startPickerOpen && { zIndex: 10, elevation: 10 }]}>
        <View style={styles.headerLeft}>
          <View style={[{ position: "relative" }, startPickerOpen && { elevation: 10, zIndex: 10 }]}>
            <View style={[styles.startTimeRow, { borderColor: colors.border }]}>
              <TextInput
                value={startText}
                onChangeText={setStartText}
                onBlur={() => commitStartText(startText)}
                onSubmitEditing={() => commitStartText(startText)}
                placeholder="e.g. Jul 23 8am"
                placeholderTextColor={colors.placeholder}
                style={[styles.startTimeInput, { color: colors.text }]}
              />
              <Button
                ref={startTimeBtnRef}
                icon="calendar-clock"
                iconSize={14}
                style={styles.startTimeCalendarBtn}
                onPress={() => setStartPickerOpen((prev) => !prev)}
              />
            </View>
            <ContextMenu
              style={{ top: 36, left: -16, zIndex: 10 }}
              visible={startPickerOpen}
              setVisible={setStartPickerOpen}
              ignoreRef={startTimeBtnRef}
            >
              <CompactDateTimePicker
                date={toUserTimezone(new Date(startTime))}
                onPick={(date) => handleStartTimeChange(date)}
              />
            </ContextMenu>
          </View>
          <View style={styles.endTimeGroup}>
            <ThemedText style={[styles.endArrow, { color: colors.textSecondary }]}>→</ThemedText>
            <ThemedText style={[styles.endLabel, { color: colors.textSecondary }]}>Ends</ThemedText>
            <ThemedText style={[styles.endValue, { color: colors.textSecondary }]}>{endDisplay}</ThemedText>
          </View>
        </View>
        <Button
          icon="plus"
          text="Add Segment"
          style={[styles.addButton, { borderColor: colors.primary }]}
          textStyle={{ fontSize: 12, color: colors.primary }}
          iconStyle={{ color: colors.primary }}
          iconSize={14}
          onPress={handleAddSegment}
        />
      </View>

      {/* Segment list */}
      <View
        style={[
          styles.list,
          { borderColor: colors.border, backgroundColor: colors.backgroundSecondary },
          { elevation: containerElevation },
        ]}
      >
        {segments.length === 0 ? (
          <View style={styles.emptyState}>
            <ThemedText style={[styles.emptyText, { color: colors.textSecondary }]}>
              No segments added. Click &quot;Add Segment&quot; to start.
            </ThemedText>
          </View>
        ) : (
          <>
            {/* Column headers */}
            <View style={[styles.columnHeaders, { borderColor: colors.border }]}>
              <View style={{ width: 12 }} />
              <ThemedText style={[styles.columnLabel, { width: 24, textAlign: "center", color: colors.textSecondary }]}>
                Bill
              </ThemedText>
              <ThemedText style={[styles.columnLabel, { width: 24, textAlign: "center", color: colors.textSecondary }]}>
                Pay
              </ThemedText>
              <ThemedText style={[styles.columnLabel, { minWidth: 113, color: colors.textSecondary }]}>Type</ThemedText>
              <ThemedText style={[styles.columnLabel, { flex: 1, color: colors.textSecondary }]}>Duration</ThemedText>
              <ThemedText style={[styles.columnLabel, { flex: 1, color: colors.textSecondary }]}>Modifiers</ThemedText>
              <View style={{ width: 34 }} />
            </View>
            {segments.map((segment, index) => (
              <SegmentRow
                key={segment.id}
                segment={segment}
                index={index}
                onUpdate={handleUpdateSegment}
                onDelete={handleDeleteSegment}
                onMenuToggle={createMenuToggleHandler(segment.id)}
                onDragStart={handleDragStart}
                onDragEnd={handleReorder}
                isDragging={draggingIndex === index}
              />
            ))}
            {/* Summary row - welded to bottom, zIndex -1 to stay under segment menus */}
            <View style={[styles.summaryRow, { borderColor: colors.border, zIndex: -1 }]}>
              <ThemedText style={[styles.summaryLabel, { color: colors.textSecondary }]}>
                Total: {summary.totalHours.toFixed(2)}h
              </ThemedText>
              <ThemedText style={[styles.summaryLabel, { color: colors.complete?.text || "#10b981" }]}>
                Worked: {summary.workedHours.toFixed(2)}h
              </ThemedText>
              {summary.mealHours > 0 && (
                <ThemedText style={[styles.summaryLabel, { color: colors.pending?.text || "#f59e0b" }]}>
                  Meal: {summary.mealHours.toFixed(2)}h
                </ThemedText>
              )}
              {summary.breakHours > 0 && (
                <ThemedText style={[styles.summaryLabel, { color: colors.neutral?.text || "#6b7280" }]}>
                  Break: {summary.breakHours.toFixed(2)}h
                </ThemedText>
              )}
              {summary.nonWorkedHours > 0 && (
                <ThemedText style={[styles.summaryLabel, { color: colors.primary || "#3b82f6" }]}>
                  NW: {summary.nonWorkedHours.toFixed(2)}h
                </ThemedText>
              )}
            </View>
          </>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 8,
    overflow: "visible",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  startTimeButton: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    minHeight: 28,
    gap: 4,
  },
  startTimeRow: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 6,
    minHeight: 28,
    paddingLeft: 8,
    gap: 4,
  },
  startTimeInput: {
    fontSize: 12,
    paddingVertical: 4,
    minWidth: 120,
    ...(Platform.OS === "web" ? { outlineStyle: "none" as const } : {}),
  },
  startTimeCalendarBtn: {
    borderWidth: 0,
    backgroundColor: "transparent",
    paddingHorizontal: 6,
  },
  endTimeGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  endArrow: {
    fontSize: 14,
  },
  endLabel: {
    fontSize: 11,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  endValue: {
    fontSize: 12,
    fontWeight: "600",
  },
  addButton: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderStyle: "dashed",
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    gap: 4,
    minHeight: 28,
  },
  list: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 8,
    overflow: "visible",
  },
  columnHeaders: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  columnLabel: {
    fontSize: 11,
    fontWeight: "500",
  },
  summaryRow: {
    flexDirection: "row",
    gap: 12,
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  summaryLabel: {
    fontSize: 11,
    fontWeight: "500",
  },
  emptyState: {
    padding: 24,
    alignItems: "center",
  },
  emptyText: {
    fontSize: 13,
    textAlign: "center",
  },
});
